import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Box, Typography, Stack, Chip, Alert, Skeleton,
  Tab, Tabs, Table, TableHead, TableBody, TableRow, TableCell,
  Tooltip, useTheme,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import KpiRow, { KpiCell } from '../components/common/KpiRow'
import StatTile from '../components/common/StatTile'
import ViewHeader from '../components/ViewHeader'
import KpiCardsSkeleton from '../Skeletons/KpiCardsSkeleton'
import TableSkeleton from '../Skeletons/TableSkeleton'
import { FS, RAIL_WIDTH, SP, V2 } from '../theme/tokens'
import { v2Tone } from '../theme/theme'
import CaseTime from '../components/common/CaseTime'
import {
  issueTypeAnzeige,
  CUSTOMER_RESOLUTION_OPTIONS,
  normalizeCustomerResolution,
} from './constants'
import { versandkostenZeilen } from './lib/versandkostenZeilen'
import { formatMoney, formatPercent } from '../utils/format'

// Render customer_resolution as label, normalizing legacy values to canonical first.
const _customerResolutionLabel = (v) => {
  if (!v) return v
  const canon = normalizeCustomerResolution(v)
  return CUSTOMER_RESOLUTION_OPTIONS.find(o => o.value === canon)?.label || v
}

const API = process.env.REACT_APP_API_BASE_URL

// Brand colors: Service = blue, Return = amber.
//
// 🔴 `C_RETURN` und `C_WARN` sind DERSELBE Wert (beide `warnText`). Vor dem
// Umbau waren es zwei Literale (orange-500 / amber-500); das Design-System hat
// aber EINEN Warnton, nicht zwei. Das ist kein Fehler — die Kategorialpalette
// darf den Wert nur nicht zweimal führen (Regel-16 B1: zwei Kuchenstuecke in
// derselben Farbe). `C_WARN` ist eine Schwellenfarbe fuer KPI-TEXT, nie eine
// Diagrammserie; deshalb faellt sein Platz in `CHART_COLORS` weg, statt ihm
// einen erfundenen Ton zu geben.
const C_SERVICE  = V2.infoText
const C_RETURN   = V2.warnText
const C_GOOD     = V2.okText
const C_WARN     = V2.warnText
const C_ERROR    = V2.errText
const C_NEUTRAL  = V2.ink45

// 🔴 Jeder Eintrag DISTINKT. Die Palette wird per Index (`i % length`) auf
// Kuchenstuecke und Chips gelegt — ein doppelter Wert rendert zwei Flaechen in
// derselben Farbe und macht die Zuordnung unmoeglich (Regel-16 B1).
export const CHART_COLORS = [C_SERVICE, C_RETURN, C_GOOD, '#818cf8', '#06b6d4', '#a78bfa']

/** Anteil eines Werts an der Summe, als BRUCH (0..1) — dieselbe Einheit, die
 *  `formatPercent` erwartet. Recharts' On-Slice-`percent` war ebenfalls ein Bruch. */
function anteil(value, data) {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0)
  return total > 0 ? (Number(value) || 0) / total : 0
}

/** Die Legende EINER Torte: Name + Anzahl + Prozent an EINEM Ort.
 *
 *  Befund 12 (2026-10-06): das On-Slice-Label lag auf demselben Platz wie die
 *  Legende darunter — Ueberlappung und Abschnitt. Es faellt weg; die
 *  Prozentzahl, die nur dort stand, wandert hierher. `data.value` ist in allen
 *  drei Torten eine ANZAHL (nicht die Prozentzahl). */
export function PieLegend({ data, colors }) {
  return (
    <Stack direction="row" spacing={SP.sm} justifyContent="center" flexWrap="wrap" useFlexGap mt={0.5}>
      {data.map((d, i) => (
        <Stack key={d.name} direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colors[i % colors.length] }} />
          <Typography variant="caption">
            {d.name}: <strong>{d.value}</strong> ({formatPercent(anteil(d.value, data), { digits: 0 })})
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

// 🔴 `awaiting_customer_info` und `escalated` waren nach dem Umbau identisch mit
// ihren Geschwister-Status (`warnText` bzw. `errText`) — vorher je zwei
// verschiedene Toene. Jetzt nimmt der zweite Status je Familie den gedaempften
// `-soft`-Ton; `reopened` rueckt auf den dritten Amber-Ton (`warnTextDark`,
// ≈ sein urspruenglicher Wert), damit es nicht mit `awaiting_customer_info`
// zusammenfaellt. Die uebrigen Doppel (confirmed/resolved,
// vendor_notified/in_transit, approved/received) sind ALTLAST und wurden vom
// Gate ausdruecklich nicht beanstandet.
export const STATUS_COLORS = {
  draft: V2.washSelected,
  vendor_notified: V2.infoSoft,
  awaiting_response: V2.warnText,
  awaiting_customer_info: V2.warnSoft,
  partially_approved: '#818cf8',
  approved: V2.okSoft,
  confirmed: V2.okText,
  rejected: V2.errText,
  escalated: V2.errSoft,
  resolved: V2.okText,
  awaiting_return: '#818cf8',
  in_transit: V2.infoSoft,
  received: V2.okSoft,
  reopened: V2.warnTextDark,
}

// Confidence-bucket colors — five DISTINCT tones (Regel-16 B1). Lifted to
// module scope so the "no two buckets share a colour" rule is testable.
export const CONFIDENCE_COLORS = [V2.okText, V2.ok100, V2.warnText, V2.warn100, V2.ink45]

/**
 * Die ANATOMIE der Berichtsreiter — das Geruest hat die Form des GELADENEN.
 *
 * 🔴 K-S (UI-Durchgang 2, 2026-10-09): bis hierher zeichnete der Ladezustand
 *    EIN Geruest aus vier Kacheln ueber einer Tabelle. Das ist die Form des
 *    Overview-Reiters, nicht die des Berichts — und ein Geruest, das eine
 *    andere Form vorwegnimmt als die eintreffenden Daten, laesst die Seite beim
 *    Laden springen, genau den Sprung, den es verhindern soll
 *    (`anti-patterns.md` §11).
 *
 *    Der Satz, der bis zum 2026-10-09 im Docstring stand — „Die
 *    Berichtsflaechen sind Kennzahl-Reihen ueber Tabellen" — beschrieb EINEN
 *    von fuenf Reitern und war fuer vier falsch.
 *
 * 🔴 Gemessen am GELADENEN Code (Regel 17), nicht aus einem Bericht
 *    uebernommen. Die Funktionen sind stabil, Zeilennummern waeren es nicht —
 *    diese Tabelle verschiebt sich mit jeder Zeile darueber:
 *
 *      | Reiter     | KPI-Reihen | darunter         |
 *      |------------|------------|------------------|
 *      | Overview   | 6 · 2      | Diagramm-Panel   |
 *      | Products   | —          | Tabelle (8 Sp.)  |
 *      | Vendors    | —          | Tabelle (8 Sp.)  |
 *      | Financial  | 4 · 2      | Diagramm-Panel   |
 *      | Automation | 5 · 3      | Diagramm-Panel   |
 *
 *    ⚠️ `AutomationTab` traegt FUENF und DREI Kacheln, nicht fuenf und eine:
 *    die Vorlage der Vorgaengerbahn nannte „5 + 1", der Code sagt drei
 *    (`Approved Labels` · `Rejected Labels` · `Ignored Emails`). Es gilt der
 *    Code.
 *
 * 🔴 Was das Geruest NICHT ist: die ANATOMIE. Es reserviert je Reiter die
 *    Kachelreihen und den ERSTEN beherrschenden Block darunter — eine
 *    UNTERGRENZE der geladenen Seite, nicht ihre Abbildung. Die weiteren Bloecke
 *    bleiben absichtlich unreserviert: ihre Zahl und ihre Hoehe haengen an
 *    Daten, die im Ladezustand nicht da sind (eine Liste mit n Zeilen, ein
 *    Diagramm mit n Serien). Ein Geruest, das sie erfunden einnimmt, waere eine
 *    zweite Behauptung ueber die Daten statt einer Form. Konkret fehlen:
 *
 *      Overview  — Status-Liste (ab `:419`) und Kreisdiagramm (ab `:446`);
 *                  reserviert ist nur der Linien-Chart (ab `:400`).
 *      Financial — ein ZWEITES Diagramm (ab `:1005`) und die Tabelle
 *                  „Open Exposure Detail" (ab `:1092`, bedingt auf
 *                  `open_exposure.length > 0`).
 *
 *    ⚠️ Der Satz, der hier bis zur Nachbesserung 2 stand — „Genau EINES von
 *    `chart`/`table` steht je Reiter, einen Reiter mit beidem gibt es nicht" —
 *    war am geladenen Code FALSCH: `FinancialTab` traegt ein Diagramm (`:988`)
 *    UND die (bedingte) Tabelle (`:1092`). Er ist gestrichen; die Tabelle oben
 *    nennt je Reiter weiterhin genau den Block, den das Geruest zeichnet.
 *
 * `rows` sind die Kachelzahlen je Reihe, `chart` die Hoehe des Diagramm-Blocks
 * unter ihnen (die Hoehe der geladenen `ResponsiveContainer`), `table` die
 * Spaltenzahl der Tabellenflaeche. Je Reiter ist hoechstens EINES der beiden
 * Felder gesetzt — `chart` dort, wo nach den Kacheln ein Diagramm kommt.
 *
 * ⚠️ `REPORT_ANATOMY[3].rows = [4, 2]` reserviert die zweite Financial-Reihe
 * IMMER; geladen steht sie nur bei `openByType.service > 0 ||
 * openByType.return > 0` (`:961`) und traegt dann EINE oder ZWEI Kacheln
 * (`:966`, `:974`). Sind beide Werte Null, faellt die reservierte Reihe beim
 * Eintreffen der Daten weg und die Seite springt nach OBEN — die Umkehrung des
 * Ziels. Das ist bewusst hingenommen (W-2 der Regel-15-Runde 1): der Regelfall
 * hat die Reihe, und die Alternative — sie gar nicht zu reservieren — springt
 * in JEDEM Fall mit Exposure.
 */
const REPORT_ANATOMY = [
  { rows: [6, 2], chart: 240 }, // 0 OverviewTab
  { rows: [], table: 8 },       // 1 ProductsTab
  { rows: [], table: 8 },       // 2 VendorsTab
  { rows: [4, 2], chart: 220 }, // 3 FinancialTab
  { rows: [5, 3], chart: 200 }, // 4 AutomationTab
]

/**
 * ChartPanelSkeleton — Ladezustand eines DIAGRAMM-Panels.
 *
 * Die dritte Flaeche der Berichtsreiter neben Kachelreihe und Tabelle: ein
 * `Panel` mit einer Titelzeile und einer Diagrammflaeche darunter. Die Hoehe
 * kommt vom Aufrufer (sie ist die der geladenen `ResponsiveContainer`), damit
 * das Geruest dieselbe Flaeche einnimmt wie das Diagramm und die Seite beim
 * Eintreffen der Daten nicht springt.
 *
 * 🔴 KEIN erfundener Text — nur Balken; der Titel ist eine Aussage ueber Daten,
 *    die noch nicht da sind (dieselbe Begruendung wie `TableSkeleton`).
 *
 * Werte aus `theme/tokens` — Rule 22 laesst neue Literale nicht zu.
 *
 * @props
 *   height    — Hoehe der Diagrammflaeche in px (die der geladenen Flaeche)
 *   ariaLabel — zugaenglicher Name des Ladezustands
 */
function ChartPanelSkeleton({ height, ariaLabel }) {
  return (
    <Panel role="status" aria-label={ariaLabel} aria-busy="true">
      <Skeleton variant="text" width="30%" />
      <Skeleton variant="rounded" height={height} sx={{ mt: SP.sm }} />
    </Panel>
  )
}

/**
 * Loader — Ladezustand des Berichts, in der Anatomie des AKTIVEN Reiters.
 *
 * Der Aufrufer nennt seinen Reiter (`tab`, Reihenfolge wie `TABS`); die Form
 * kommt aus `REPORT_ANATOMY`. Ohne Reiter steht die Overview-Form.
 *
 * `role="status"` + `aria-busy` melden den Ladezustand Hilfsmitteln an — die
 * Kachelreihen tun es selbst, das Diagramm-Panel hier. Der `aria-label` der
 * Kachelreihen traegt ihre Nummer, damit eine Probe die zwei Reihen EINES
 * Reiters unterscheiden kann.
 *
 * @props
 *   tab — Index des aktiven Reiters (Vorgabe 0)
 */
function Loader({ tab = 0 }) {
  const { rows = [], chart, table } = REPORT_ANATOMY[tab] || REPORT_ANATOMY[0]
  return (
    <Stack spacing={SP.card}>
      {rows.map((cards, i) => (
        <KpiCardsSkeleton
          key={i}
          cards={cards}
          ariaLabel={`Loading report metrics ${i + 1}`}
        />
      ))}
      {table
        ? <TableSkeleton columns={table} rows={5} ariaLabel="Loading report table" />
        : <ChartPanelSkeleton height={chart} ariaLabel="Loading report chart" />}
    </Stack>
  )
}

/**
 * KpiCard — EINE Kachel einer KPI-Reihe.
 *
 * 🔴 Seit dem 2026-10-07 (UI-Durchgang 2) eine duenne Huelle ueber den geteilten
 *    Bausteinen `KpiRow` / `KpiCell` / `StatTile` statt einer eigenen Bauart.
 *    Die eigene Fassung war ZENTRIERT, stellte den WERT ueber die Beschriftung
 *    und malte ihn in `variant="h4"` — der dritte KPI-Stil im Haus neben
 *    `KpiRow` (die Norm: linksbuendig, Beschriftung ueber dem Wert) und den
 *    Grossbuchstaben-Kacheln der Faelligkeitsseite.
 *
 *    `StatTile` traegt genau diese Angaben: `label` / `value` / `hint` (aus
 *    `sub`) / `valueColor` (aus `color`). Das Symbol sitzt im `right`-Slot der
 *    Beschriftungszeile — die einzige Angabe, fuer die `StatTile` keinen
 *    eigenen Platz hat.
 *
 * 🔴 Die AUFRUFSTELLEN bleiben woertlich unveraendert (`label`/`value`/`sub`/
 *    `color`/`icon`): die Umstellung liegt in dieser Huelle und in den sechs
 *    Reihen-Rahmen, die jetzt `KpiRow` sind. Wer die Anatomie anfasst, fasst
 *    sie an EINER Stelle an.
 */
function KpiCard({ label, value, sub, color, icon }) {
  return (
    <KpiCell>
      <StatTile label={label} value={value} hint={sub} valueColor={color} right={icon} />
    </KpiCell>
  )
}

function TypeBadge({ type }) {
  return (
    <Chip
      size="small"
      label={type === 'service' ? 'Service' : 'Return'}
      sx={{
        fontSize: '0.65rem', height: 18, fontWeight: 600,
        bgcolor: type === 'service' ? `${C_SERVICE}22` : `${C_RETURN}22`,
        color: type === 'service' ? C_SERVICE : C_RETURN,
        border: `1px solid ${type === 'service' ? C_SERVICE : C_RETURN}44`,
      }}
    />
  )
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------
function OverviewTab() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API}/api/rma/reports/overview`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Loader tab={0} />
  if (!data) return <Alert severity="error">Failed to load overview data.</Alert>

  const serviceOpen = data.by_type_open?.service || 0
  const returnOpen  = data.by_type_open?.return  || 0
  const slaText = data.sla_compliance_pct != null ? `${data.sla_compliance_pct}%` : '—'
  const slaColor = data.sla_compliance_pct == null ? undefined
    : data.sla_compliance_pct >= 80 ? 'success.main'
    : data.sla_compliance_pct >= 60 ? 'warning.main' : 'error.main'

  const statusEntries = Object.entries(data.by_status || {})
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count)

  // Pie data: service vs return open
  const typePieData = [
    { name: 'Service', value: serviceOpen },
    { name: 'Return',  value: returnOpen  },
  ].filter(d => d.value > 0)
  const typePieColors = [C_SERVICE, C_RETURN]

  return (
    <Stack spacing={SP.section}>

      {/* Headline KPI row */}
      {/* 🔴 R16 W1/W5 (Nachbesserung 2, 2026-10-09): `maxMobile={Infinity}` —
          sechs Kacheln, die fuenfte (ohne `Infinity` verborgene) traegt eine Statusfarbe
          (`color={slaColor}` an „SLA Compliance"). Die Desktop-Reihenfolge
          bleibt unveraendert. Regel und Flaechenliste: `KpiRow.jsx`
          `@props maxMobile`, `pattern-library.md` §KPI. */}
      <KpiRow maxMobile={Infinity}>
        <KpiCard
          label="Open Service Cases"
          value={serviceOpen}
          color={serviceOpen > 0 ? C_SERVICE : undefined}
        />
        <KpiCard
          label="Open Return Cases"
          value={returnOpen}
          color={returnOpen > 0 ? C_RETURN : undefined}
        />
        <KpiCard
          label="Overdue"
          value={data.overdue_count || 0}
          color={(data.overdue_count || 0) > 0 ? C_ERROR : undefined}
          sub="past SLA due date"
        />
        <KpiCard
          label="Resolved This Month"
          value={data.resolved_this_month || 0}
          color={(data.resolved_this_month || 0) > 0 ? C_GOOD : undefined}
        />
        <KpiCard
          label="SLA Compliance"
          value={slaText}
          color={slaColor}
          sub="resolved on time"
        />
        <KpiCard
          label="Avg Resolution"
          value={
            data.avg_resolution_days?.service != null || data.avg_resolution_days?.return != null
              ? `${((data.avg_resolution_days?.service || 0) + (data.avg_resolution_days?.return || 0)) / (
                  (data.avg_resolution_days?.service != null ? 1 : 0) + (data.avg_resolution_days?.return != null ? 1 : 0)
                ) | 0}d`
              : '—'
          }
          sub={[
            data.avg_resolution_days?.service != null ? `Svc ${data.avg_resolution_days.service}d` : null,
            data.avg_resolution_days?.return  != null ? `Ret ${data.avg_resolution_days.return}d`  : null,
          ].filter(Boolean).join(' · ')}
        />
      </KpiRow>

      {/* Financial snapshot */}
      <KpiRow>
        <KpiCard
          label="Outstanding Credit"
          value={formatMoney(data.outstanding_credit_eur || 0, { digits: 0 })}
          sub="approved, not yet paid"
          color={(data.outstanding_credit_eur || 0) > 0 ? C_WARN : undefined}
        />
        <KpiCard
          label="Recovered YTD"
          value={formatMoney(data.resolved_ytd_eur || 0, { digits: 0 })}
          sub="from resolved cases"
          color={C_GOOD}
        />
      </KpiRow>

      {/* Monthly trend — service/return split */}
      <Panel>
        <Typography variant="subtitle2" mb={SP.sm}>Monthly Case Volume — Service vs Return</Typography>
        {!data.monthly?.length ? (
          <Typography variant="body2" color="text.secondary">No data yet.</Typography>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={V2.wash} />
              <XAxis dataKey="month" tick={{ fontSize: FS.xs }} />
              <YAxis tick={{ fontSize: FS.xs }} allowDecimals={false} />
              <ReTooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: FS.xs }} />
              <Bar dataKey="service_opened"   name="Service Opened"   fill={C_SERVICE}           stackId="open" radius={[0,0,0,0]} />
              <Bar dataKey="return_opened"    name="Return Opened"    fill={C_RETURN}            stackId="open" radius={[3,3,0,0]} />
              <Bar dataKey="service_resolved" name="Service Resolved" fill={`${C_SERVICE}77`}    stackId="res"  radius={[0,0,0,0]} />
              <Bar dataKey="return_resolved"  name="Return Resolved"  fill={`${C_RETURN}77`}     stackId="res"  radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      {/* Status breakdown + open type donut */}
      <Stack direction="row" spacing={SP.section} flexWrap="wrap" useFlexGap>

        <Panel sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2" mb={SP.sm}>Cases by Status (All Time)</Typography>
          {!statusEntries.length ? (
            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
          ) : (
            <Stack spacing={SP.chip}>
              {statusEntries.map(({ status, count }) => (
                <Stack key={status} direction="row" justifyContent="space-between" alignItems="center">
                  <Chip
                    label={status.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      bgcolor: STATUS_COLORS[status] || C_NEUTRAL,
                      color: STATUS_TEXT_COLORS[status] || CHIP_TEXT_ON_DARK,
                      fontWeight: 600,
                      fontSize: FS.xs,
                      textTransform: 'capitalize', border: '1px solid', borderColor: 'divider',
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>{count}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Panel>

        {typePieData.length > 0 && (
          <Panel sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" mb={SP.sm}>Open Cases by Type</Typography>
            <Stack direction="row" spacing={SP.sm} alignItems="center">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={typePieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={65}
                    // Befund 12 (2026-10-06): das On-Slice-Label faellt weg — es
                    // lag auf demselben Platz wie die Legende darunter. Name,
                    // Anzahl und Prozent stehen jetzt in der Legende.
                  >
                    {typePieData.map((_, i) => (
                      <Cell key={i} fill={typePieColors[i]} />
                    ))}
                  </Pie>
                  <ReTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Stack>
            <PieLegend data={typePieData} colors={typePieColors} />
          </Panel>
        )}
      </Stack>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Products tab
// ---------------------------------------------------------------------------
function ProductsTab() {
  const [products, setProducts] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    axios.get(`${API}/api/rma/reports/products`)
      .then(r => { setProducts(r.data); if (r.data.length) setSelected(0) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Loader tab={1} />
  if (!products) return <Alert severity="error">Failed to load product data.</Alert>
  if (!products.length) return (
    <Typography color="text.secondary" py={4} textAlign="center">No product data yet.</Typography>
  )

  const alerts = products.filter(p => p.is_alert)
  const selectedProduct = selected != null ? products[selected] : null

  // Build issue breakdown chart data for selected product
  const issueBreakdown = selectedProduct
    ? Object.entries(selectedProduct.issue_breakdown || {}).flatMap(([caseType, issues]) =>
        Object.entries(issues).map(([issueType, count]) => ({
          // N2b: `fallback: issueType` haelt die Legende wertgleich — eine
          // Diagrammgruppe ohne Namen waere schlechter als ihr roher Schluessel.
          name: issueTypeAnzeige(issueType, { fallback: issueType }),
          count,
          caseType,
        }))
      )
    : []

  return (
    <Stack spacing={SP.section}>

      {/* Alert strip */}
      {alerts.length > 0 && (
        <Panel sx={{ borderColor: 'error.light' }}>
          <Stack direction="row" spacing={SP.tight} alignItems="center" mb={SP.sm}>
            <WarningAmberIcon color="error" fontSize="small" />
            <Typography variant="subtitle2" color="error.main">
              Quality Alerts — {alerts.length} product{alerts.length !== 1 ? 's' : ''} flagged
            </Typography>
          </Stack>
          <Stack spacing={SP.tight}>
            {alerts.map((p, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" fontWeight={600}>{p.product_title}</Typography>
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">{p.sku}</Typography>
                </Box>
                <Stack direction="row" spacing={SP.chip} alignItems="center">
                  <Typography variant="caption" color="text.secondary">{p.alert_reason}</Typography>
                  <Chip size="small" color="error" label="Alert" />
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Panel>
      )}

      {/* Product table */}
      <Panel>
        <Typography variant="subtitle2" mb={SP.sm}>Products by Case Volume</Typography>
        <Box overflow="auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product / SKU</TableCell>
                <TableCell align="right">
                  <Tooltip title="Service request cases"><span style={{ color: C_SERVICE }}>Service</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Defective return cases"><span style={{ color: C_ERROR }}>Def. Returns</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Voluntary return cases"><span style={{ color: C_RETURN }}>Vol. Returns</span></Tooltip>
                </TableCell>
                <TableCell align="right">30d</TableCell>
                <TableCell align="right">90d</TableCell>
                <TableCell align="right">Avg Days</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p, i) => (
                <TableRow
                  key={i}
                  hover
                  selected={selected === i}
                  onClick={() => setSelected(i === selected ? null : i)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: p.is_alert ? '#fff5f5' : undefined,
                    borderLeft: p.is_alert ? `${RAIL_WIDTH}px solid ${C_ERROR}` : `${RAIL_WIDTH}px solid transparent`,
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={SP.chip} alignItems="flex-start">
                      {p.is_alert && <WarningAmberIcon sx={{ fontSize: 14, color: C_ERROR, mt: '2px', flexShrink: 0 }} />}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{p.product_title}</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{p.sku}</Typography>
                        {p.vendor_key && (
                          <Typography variant="caption" color="text.disabled" display="block">{p.vendor_key}</Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={p.service_cases > 0 ? C_SERVICE : 'text.disabled'} fontWeight={p.service_cases > 0 ? 600 : 400}>
                      {p.service_cases}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={p.defective_returns > 0 ? C_ERROR : 'text.disabled'} fontWeight={p.defective_returns > 0 ? 600 : 400}>
                      {p.defective_returns}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={p.voluntary_returns > 0 ? C_RETURN : 'text.disabled'}>
                      {p.voluntary_returns}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={p.total_30d >= 3 ? C_ERROR : p.total_30d >= 2 ? C_WARN : 'text.primary'} fontWeight={p.total_30d >= 2 ? 700 : 400}>
                      {p.total_30d}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{p.total_90d}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {p.avg_resolution_days != null ? `${p.avg_resolution_days}d` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.disabled">
                      {selected === i ? '▲' : '▼'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Panel>

      {/* Selected product drill-down */}
      {selectedProduct && (
        <Panel>
          <Typography variant="subtitle2" mb={SP.sm}>
            {selectedProduct.product_title}
            {selectedProduct.sku !== 'N/A' && (
              <Typography component="span" variant="caption" color="text.secondary" fontFamily="monospace" ml={1}>
                {selectedProduct.sku}
              </Typography>
            )}
          </Typography>

          <Stack direction="row" spacing={SP.section} flexWrap="wrap" useFlexGap>
            {/* Stats */}
            <Stack spacing={SP.chip} sx={{ minWidth: 180 }}>
              {[
                ['Service Cases',     selectedProduct.service_cases,     C_SERVICE],
                ['Defective Returns', selectedProduct.defective_returns,  C_ERROR],
                ['Voluntary Returns', selectedProduct.voluntary_returns,  C_RETURN],
                ['Unique Orders',     selectedProduct.unique_orders,      undefined],
                // Track A5-4b: both come off `MIN(created_at)::date` /
                // `MAX(created_at)::date` (rma_routes.py:13855-13856), i.e.
                // bare `YYYY-MM-DD` — a DAY, so `dateOnly`. <CaseTime> already
                // renders the em-dash placeholder for a null, so the ternary is
                // gone rather than duplicated.
                ['First Case',        <CaseTime value={selectedProduct.first_case_date} dateOnly variant="absolute" />, undefined],
                ['Latest Case',       <CaseTime value={selectedProduct.latest_case_date} dateOnly variant="absolute" />, undefined],
              ].map(([label, value, color]) => (
                <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="caption" fontWeight={600} color={color || 'text.primary'}>{value}</Typography>
                </Stack>
              ))}
            </Stack>

            {/* Issue breakdown chart */}
            {issueBreakdown.length > 0 && (
              <Box flex={1} minWidth={220}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Issue breakdown</Typography>
                <ResponsiveContainer width="100%" height={Math.max(issueBreakdown.length * 28, 80)}>
                  <BarChart
                    data={issueBreakdown}
                    layout="vertical"
                    margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
                  >
                    <XAxis type="number" tick={{ fontSize: FS.xs }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: FS.xs }} width={110} />
                    <ReTooltip />
                    <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                      {issueBreakdown.map((entry, i) => (
                        <Cell key={i} fill={entry.caseType === 'service' ? C_SERVICE : C_RETURN} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <Stack direction="row" spacing={SP.sm} mt={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: C_SERVICE }} />
                    <Typography variant="caption">Service</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: C_RETURN }} />
                    <Typography variant="caption">Return</Typography>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </Panel>
      )}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Vendors tab
// ---------------------------------------------------------------------------
function VendorsTab() {
  const [vendors, setVendors] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    axios.get(`${API}/api/rma/reports/vendor`)
      .then(r => { setVendors(r.data); if (r.data.length) setSelected(r.data[0].vendor_key) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Loader tab={2} />
  if (!vendors) return <Alert severity="error">Failed to load vendor data.</Alert>
  if (!vendors.length) return <Typography color="text.secondary">No vendor data yet.</Typography>

  const vendor = vendors.find(v => v.vendor_key === selected) || vendors[0]
  const issueData = Object.entries(vendor.issue_breakdown || {}).map(([k, v]) => ({
    name: issueTypeAnzeige(k, { fallback: k }),   // N2b, wie oben
    value: v,
  }))

  return (
    <Stack spacing={SP.section}>

      {/* Vendor scorecard table */}
      <Panel>
        <Box overflow="auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right" sx={{ color: C_SERVICE }}>Service</TableCell>
                <TableCell align="right" sx={{ color: C_RETURN }}>Returns</TableCell>
                <TableCell align="right">Approval %</TableCell>
                <TableCell align="right">Avg Days</TableCell>
                <TableCell align="right">Recovered €</TableCell>
                <TableCell align="right">Overdue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map(v => (
                <TableRow
                  key={v.vendor_key}
                  hover
                  selected={v.vendor_key === selected}
                  onClick={() => setSelected(v.vendor_key)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{v.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {v.vendor_key}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{v.total_cases}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={v.service_cases > 0 ? C_SERVICE : 'text.disabled'}>
                      {v.service_cases}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={v.return_cases > 0 ? C_RETURN : 'text.disabled'}>
                      {v.return_cases}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={v.approval_rate_pct >= 70 ? 'success.main' : v.approval_rate_pct >= 40 ? 'warning.main' : 'error.main'}
                      fontWeight={600}
                    >
                      {v.approval_rate_pct}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {v.avg_days_to_resolve != null ? `${v.avg_days_to_resolve}d` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {v.total_value_eur > 0 ? formatMoney(v.total_value_eur) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {v.overdue_count > 0 ? (
                      <Chip label={v.overdue_count} color="error" size="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">0</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Panel>

      {/* Selected vendor detail */}
      {vendor && (
        <Stack direction="row" spacing={SP.section} flexWrap="wrap" useFlexGap>

          <Panel sx={{ flex: 2, minWidth: 280 }}>
            <Typography variant="subtitle2" mb={SP.sm}>
              {vendor.display_name} — Monthly Cases
            </Typography>
            {!vendor.monthly_trend?.length ? (
              <Typography variant="body2" color="text.secondary">No history yet.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={vendor.monthly_trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={V2.wash} />
                  <XAxis dataKey="month" tick={{ fontSize: FS.xs }} />
                  <YAxis tick={{ fontSize: FS.xs }} allowDecimals={false} />
                  <ReTooltip />
                  <Line type="monotone" dataKey="count" stroke={C_SERVICE} dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" mb={SP.sm}>Issue Breakdown</Typography>
            {!issueData.length ? (
              <Typography variant="body2" color="text.secondary">No data.</Typography>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={issueData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      // Befund 12: kein On-Slice-Label — diese Torte hatte
                      // ueberhaupt keine Legende, das Label trug den ganzen
                      // Namen UND den Prozentwert. Beides steht jetzt darunter.
                    >
                      {issueData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <PieLegend data={issueData} colors={CHART_COLORS} />
              </>
            )}
          </Panel>
        </Stack>
      )}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Financial tab
// ---------------------------------------------------------------------------
function FinancialTab() {
  const theme = useTheme()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API}/api/rma/reports/financial`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Loader tab={3} />
  if (!data) return <Alert severity="error">Failed to load financial data.</Alert>

  const s = data.summary || {}
  const sh = data.shipping || {}

  // Aggregate open exposure by case_type
  const openByType = { service: 0, return: 0 }
  ;(data.open_exposure || []).forEach(r => {
    openByType[r.case_type] = (openByType[r.case_type] || 0) + r.total_eur
  })

  // Resolution donut data (resolved cases). Post-migration 124 (2026-05-21):
  // for pending-customer-resolution rows we prefer the canonical
  // vendor_decision + compensation_kind axes from rma_vendor_credits (emitted
  // by /reports/financial alongside the legacy resolution_type key). Falls
  // back to the legacy resolution_type for cases that pre-date the backfill.
  const resolutionPie = {}
  ;(data.resolved_breakdown || []).forEach(r => {
    let label
    if (r.customer_resolution === 'pending') {
      if (r.vendor_decision === 'rejected') {
        label = 'rejected'
      } else if (r.compensation_kind) {
        label = r.compensation_kind
      } else if (r.resolution_type && r.resolution_type !== 'pending') {
        // Fallback for historical cases without a vendor_credit row.
        label = `${r.resolution_type} (legacy)`
      } else {
        label = 'pending'
      }
    } else {
      label = _customerResolutionLabel(r.customer_resolution)
    }
    resolutionPie[label] = (resolutionPie[label] || 0) + r.case_count
  })
  const resPieData = Object.entries(resolutionPie)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }))

  return (
    <Stack spacing={SP.section}>

      {/* Headline financials */}
      <KpiRow>
        <KpiCard
          label="Open Credit Exposure"
          value={formatMoney(s.open_credit_eur || 0, { digits: 0 })}
          sub="approved, not yet paid"
          color={(s.open_credit_eur || 0) > 0 ? C_WARN : undefined}
        />
        <KpiCard
          label="Recovered YTD"
          value={formatMoney(s.recovered_ytd_eur || 0, { digits: 0 })}
          sub="from resolved cases"
          color={C_GOOD}
        />
        {/* D8-Torrunde: der Zaehler dahinter umfasst `refund` UND
            `partial_refund` (REFUND_BEARING_RESOLUTIONS, abgeleitet in
            `rma_routes.py::_REFUND_RESOLUTION_SQL_IN`). Der Untertitel sagt es,
            weil die Kachel sonst eine andere Aussage traefe als ihre Zahl. */}
        <KpiCard
          label="Pending Refunds"
          value={s.pending_refund_count || 0}
          sub="open cases, full or partial refund agreed"
          color={(s.pending_refund_count || 0) > 0 ? C_WARN : undefined}
        />
        <KpiCard
          label="Pending Replacements"
          value={s.pending_replacement_count || 0}
          sub="open cases, replacement agreed"
          color={(s.pending_replacement_count || 0) > 0 ? C_WARN : undefined}
        />
      </KpiRow>

      {/* Open exposure by type */}
      {(openByType.service > 0 || openByType.return > 0) && (
        <KpiRow>
          {openByType.service > 0 && (
            <KpiCard
              label="Service Exposure"
              value={formatMoney(openByType.service, { digits: 0 })}
              sub="open service cases"
              color={C_SERVICE}
            />
          )}
          {openByType.return > 0 && (
            <KpiCard
              label="Return Exposure"
              value={formatMoney(openByType.return, { digits: 0 })}
              sub="open return cases"
              color={C_RETURN}
            />
          )}
        </KpiRow>
      )}

      {/* Monthly recovery trend */}
      <Panel>
        <Typography variant="subtitle2" mb={SP.sm}>Monthly Recovery — Service vs Return Credit</Typography>
        {!data.monthly?.length ? (
          <Typography variant="body2" color="text.secondary">No resolved cases yet.</Typography>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={V2.wash} />
              <XAxis dataKey="month" tick={{ fontSize: FS.xs }} />
              <YAxis tick={{ fontSize: FS.xs }} tickFormatter={v => `€${v}`} />
              <ReTooltip formatter={(v, name) => [formatMoney(v), name]} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: FS.xs }} />
              <Bar dataKey="service_credit_eur" name="Service Credit" fill={C_SERVICE} stackId="a" radius={[0,0,0,0]} />
              <Bar dataKey="return_credit_eur"  name="Return Credit"  fill={C_RETURN}  stackId="a" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      {/* Resolution breakdown + shipping costs */}
      <Stack direction="row" spacing={SP.section} flexWrap="wrap" useFlexGap>

        {/* Resolution donut */}
        <Panel sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="subtitle2" mb={SP.sm}>Resolution Outcomes (Resolved Cases)</Typography>
          {!resPieData.length ? (
            <Typography variant="body2" color="text.secondary">No resolved cases yet.</Typography>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={resPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={65}
                    // Befund 12: kein On-Slice-Label — diese Torte hatte keine
                    // Legende; Name und Prozent stehen jetzt darunter.
                  >
                    {resPieData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip />
                </PieChart>
              </ResponsiveContainer>
              <PieLegend data={resPieData} colors={CHART_COLORS} />
            </>
          )}
        </Panel>

        {/* Return shipping cost summary */}
        <Panel sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="subtitle2">Return Shipping Costs</Typography>
          {/* W8: beide Abfragen filtern `case_type = 'return'`; 65 der 80 Faelle
              der vier Wege liegen auf service/inquiry und stehen hier nicht. */}
          <Typography variant="caption" color="text.secondary" display="block" mb={SP.sm}>
            return cases only
          </Typography>
          <Stack spacing={SP.chip}>
            {versandkostenZeilen(sh, {
              // 🔴 Regel-16-Gate W7 (2026-09-29): die Kachel liest die
              // SEMANTISCHEN, modusabhaengigen Toene (`v2Tone(theme, …)`), nicht
              // die statischen `C_*`-Kategorialfarben dieser Datei. Die `C_*`
              // sind fuer Diagrammflaechen gedacht; Text auf Weiss gehoert auf
              // die Texttoene der Palette (ok/warn/err), die in BEIDEN Modi
              // ueber der WCAG-Schwelle liegen (tokens.js).
              // 🔴 Zwei Zeilen, zwei Farben (Regel-16-Gate W3, Owner §E8): die
              // WARNFARBE traegt „nicht bestimmt" — die offene Arbeit, wofuer
              // `warn` in dieser Datei sonst auch steht. Die zugesagte Null
              // ist entschieden und bekommt die gedaempfte Farbe.
              gut: v2Tone(theme, 'ok').main,
              fehler: v2Tone(theme, 'err').main,
              warn: v2Tone(theme, 'warn').main,
              gedaempft: 'text.secondary',
            }).map(([label, value, color, optionen]) => (
              // Teilzeilen (B1/W2) ruecken ein — die Beschriftung sagt „of
              // which"; der Einzug ist die zweite, leisere Andeutung. Klein
              // gehalten (12 px): die Labels sind lang, und ein groesserer
              // Einzug liesse sie bei 1100 px haeufiger umbrechen.
              <Stack key={label} sx={{ pl: optionen?.teilzeile ? SP.sm : 0 }}>
                {/* W3: `gap` + rechtsbuendiger Wert — ohne beides schrumpfen
                    beide Typographies in `space-between` und der Wert bricht
                    linksbuendig um (gemessen 389 px bei ~327 px verfuegbar). */}
                <Stack direction="row" justifyContent="space-between" gap={SP.sm}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  {/* W7: `tabular-nums` wie StatTile/MoneyGuardsCard, damit die
                      Wertspalte eine Kante behaelt. */}
                  <Typography variant="caption" fontWeight={600}
                    sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                    color={color || 'text.primary'}>{value ?? '—'}</Typography>
                </Stack>
                {/* W3/W4: der Zusatz der Schaetzung steht als ZWEITE, gedaempfte
                    Zeile statt am Wert — er brach die Zeile sonst um. */}
                {optionen?.hinweis && (
                  <Typography variant="caption" color="text.secondary" display="block"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {optionen.hinweis}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Panel>
      </Stack>

      {/* Open exposure detail table */}
      {data.open_exposure?.length > 0 && (
        <Panel>
          <Typography variant="subtitle2" mb={SP.sm}>Open Exposure Detail</Typography>
          <Box overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Customer Resolution</TableCell>
                  <TableCell>Vendor Decision</TableCell>
                  <TableCell align="right">Cases</TableCell>
                  <TableCell align="right">Value €</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.open_exposure.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell><TypeBadge type={r.case_type} /></TableCell>
                    <TableCell>
                      <Typography variant="body2">{_customerResolutionLabel(r.customer_resolution) || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {/* Post-migration 124: sources vendor_decision + compensation_kind
                          from the latest rma_vendor_credit per case. Falls back to the
                          legacy resolution_type column for cases that pre-date the
                          backfill so historical rows still render. */}
                      <Typography variant="body2">
                        {r.vendor_decision === 'rejected'
                          ? 'rejected'
                          : (r.compensation_kind || r.resolution_type || '—')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{r.case_count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {r.total_eur > 0 ? formatMoney(r.total_eur) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      )}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Automation tab
// ---------------------------------------------------------------------------
function AutomationTab() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API}/api/rma/reports/automation`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <Loader tab={4} />
  if (!data) return <Alert severity="error">Failed to load automation data.</Alert>

  const s = data.stats_30d || {}
  const t = data.training || {}

  const matchRateColor = s.match_rate_pct >= 70 ? C_GOOD : s.match_rate_pct >= 50 ? C_WARN : C_ERROR
  const autoRateColor  = s.auto_action_rate_pct >= 60 ? C_GOOD : s.auto_action_rate_pct >= 40 ? C_WARN : C_ERROR
  const coverageColor  = t.coverage_pct >= 70 ? C_GOOD : t.coverage_pct >= 40 ? C_WARN : C_ERROR

  // Confidence distribution colors — five distinct tones (Regel-16 B1). Vor dem
  // Umbau fuenf Literale (green-500/400, amber-500, orange-400, slate-400),
  // danach auf drei zusammengefallen — zwei Buckets je Paar waren nicht mehr
  // unterscheidbar. Definition und Begruendung stehen in `CONFIDENCE_COLORS`.
  const confColors = CONFIDENCE_COLORS

  return (
    <Stack spacing={SP.section}>

      {/* 30-day KPIs */}
      {/* 🔴 R16 W1/W5 (Nachbesserung 2, 2026-10-09): `maxMobile={Infinity}` —
          fuenf Kacheln, die fuenfte („Training Coverage") traegt
          `color={coverageColor}`. Regel und Flaechenliste: `KpiRow.jsx`
          `@props maxMobile`, `pattern-library.md` §KPI. */}
      <KpiRow maxMobile={Infinity}>
        <KpiCard
          label="Emails (30d)"
          value={s.total || 0}
          sub="received in inbox"
        />
        <KpiCard
          label="Match Rate"
          value={`${s.match_rate_pct || 0}%`}
          sub={`${s.matched || 0} matched`}
          color={matchRateColor}
        />
        <KpiCard
          label="Auto-Action Rate"
          value={`${s.auto_action_rate_pct || 0}%`}
          sub={`${s.auto_actioned || 0} auto-processed`}
          color={autoRateColor}
        />
        <KpiCard
          label="Wrong Matches"
          value={s.wrong_matches || 0}
          sub="manually unlinked"
          color={(s.wrong_matches || 0) > 0 ? C_ERROR : C_GOOD}
          icon={(s.wrong_matches || 0) === 0 ? <CheckCircleIcon fontSize="small" /> : undefined}
        />
        <KpiCard
          label="Training Coverage"
          value={`${t.coverage_pct || 0}%`}
          sub={`${t.labeled || 0} / ${t.total_matched || 0} labeled`}
          color={coverageColor}
          icon={<SmartToyIcon fontSize="small" />}
        />
      </KpiRow>

      {/* Training breakdown */}
      <KpiRow>
        <KpiCard label="Approved Labels" value={t.approved || 0} color={C_GOOD} />
        <KpiCard label="Rejected Labels" value={t.rejected || 0} color={C_ERROR} />
        <KpiCard label="Ignored Emails (30d)" value={s.ignored || 0} />
      </KpiRow>

      {/* Confidence distribution + intent breakdown */}
      <Stack direction="row" spacing={SP.section} flexWrap="wrap" useFlexGap>

        <Panel sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2" mb={SP.sm}>Confidence Distribution (90 days)</Typography>
          {!data.confidence_distribution?.length ? (
            <Typography variant="body2" color="text.secondary">No matched emails yet.</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(data.confidence_distribution.length * 36, 120)}>
              <BarChart
                data={data.confidence_distribution}
                layout="vertical"
                margin={{ top: 0, right: 8, left: 0, bottom: 0 }}
              >
                <XAxis type="number" tick={{ fontSize: FS.xs }} allowDecimals={false} />
                <YAxis type="category" dataKey="bucket" tick={{ fontSize: FS.xs }} width={60} />
                <ReTooltip />
                <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                  {data.confidence_distribution.map((_, i) => (
                    <Cell key={i} fill={confColors[i] || C_NEUTRAL} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2" mb={SP.sm}>Intent Breakdown (30 days)</Typography>
          {!data.intent_breakdown?.length ? (
            <Typography variant="body2" color="text.secondary">No intents detected yet.</Typography>
          ) : (
            <Stack spacing={SP.chip}>
              {data.intent_breakdown.map((item, i) => (
                <Stack key={item.intent} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {item.intent}
                  </Typography>
                  <Chip
                    label={item.count}
                    size="small"
                    sx={{ bgcolor: CHART_COLORS[i % CHART_COLORS.length], color: '#fff', fontWeight: 700, fontSize: FS.xs }}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Panel>
      </Stack>

      {/* Monthly automation trend */}
      <Panel>
        <Typography variant="subtitle2" mb={SP.sm}>Inbox Trend — Received / Matched / Auto-Actioned (6 months)</Typography>
        {!data.monthly?.length ? (
          <Typography variant="body2" color="text.secondary">No data yet.</Typography>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={V2.wash} />
              <XAxis dataKey="month" tick={{ fontSize: FS.xs }} />
              <YAxis tick={{ fontSize: FS.xs }} allowDecimals={false} />
              <ReTooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: FS.xs }} />
              <Line type="monotone" dataKey="total"        name="Received"     stroke={C_NEUTRAL}  dot={false} strokeWidth={1.5} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="matched"      name="Matched"      stroke={C_SERVICE}  dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="auto_actioned" name="Auto-Actioned" stroke={C_GOOD}   dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Main Reports page
// ---------------------------------------------------------------------------
const TABS = [
  { label: 'Overview' },
  { label: 'Products' },
  { label: 'Vendors' },
  { label: 'Financial' },
  { label: 'Automation' },
]

export default function RMAReports() {
  const [tab, setTab] = useState(0)

  return (
    <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <ViewHeader
        title="After-Sales Reports"
        breadcrumbs={[{ label: 'After-Sales', href: '/rma' }, { label: 'Reports' }]}
        // 🔴 Der „Export CSV"-Knopf ist am 2026-09-07 mit N14 entfallen
        // (Owner-Entscheid E11). Er war ein `<a href>` auf
        // `/api/rma/cases/export.csv` und konnte keinen `Authorization`-Kopf
        // mitschicken; der Pfad stand nicht auf der Freiliste in
        // `backend/global_auth_gate.py`. Seit dem 30.04.2026 -- dem Tag, an dem
        // der Torwaechter fuer alle `/api/*`-Pfade scharf gestellt wurde --
        // antwortete es deshalb 401. Davor lief der Knopf rund einen Monat
        // (eingefuehrt am 30.03.2026); 'hat nie funktioniert' waere falsch.
        // Genau daran hing die Einstufung `tot` im Endpunkt-Register.
        // Ein funktionierender Export waere eine kleine Arbeit (axios + Blob
        // oder ein signierter Link wie bei `/share/`) -- er ist bewusst NICHT
        // Teil von N14, siehe docs/changelog/2026-09-07-e11-endpunkte-geloescht.md.
      />

      <Panel pad="none" clip>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: SP.card, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map(t => <Tab key={t.label} label={t.label} />)}
        </Tabs>
        <Box p={SP.card}>
          {tab === 0 && <OverviewTab />}
          {tab === 1 && <ProductsTab />}
          {tab === 2 && <VendorsTab />}
          {tab === 3 && <FinancialTab />}
          {tab === 4 && <AutomationTab />}
        </Box>
      </Panel>
    </PageContainer>
  )
}

// ── Die Chip-VORDERGRUNDFARBE je Status — Regel-16 F2. ────────────────────
//
// 🔴 Steht ABSICHTLICH am Dateiende, nicht neben `STATUS_COLORS`: eine
// Einfuegung weiter oben verschiebt jede Zeile darunter, und auf diese Datei
// zeigen Zeiger aus `docs/csm/07a-endpunkt-inventar.md` (`:127`, `:317`,
// `:679`, `:911`) und `docs/csm/25-d13a-reihenfolge-2026-09-10.md` (`:720`).
// Ein Block am Ende verschiebt nichts. `const` wird erst beim Aufruf gelesen,
// nicht bei der Modulauswertung — die Verwendung im JSX oben ist damit sicher.
//
// Der Befund: die Statuschips sind `bgcolor: STATUS_COLORS[status]` mit fester
// weisser Schrift. Fuer die dunklen Fuellungen (warnText, okText, errText) ist
// das richtig; fuer die HELLEN war es unlesbar — weiss auf `warn100` (#fde68a)
// mass **1,25:1**, weiss auf `ok100` (#bbf7d0) **1,21:1**, auf `reopened`
// (`warnTextDark`) **1,67:1**. Alle unter der 3:1-Schwelle fuer Nicht-Text und
// weit unter den 4,5:1 fuer Text.
//
// 🔴 Die Reparatur folgt dem `Pill`-Baustein (`Pill.jsx:236-248`): die
// `-100`-Toene sind RAHMEN-Toene, keine Traeger von Text — eine Fuellung auf
// `-100` mit dem `-Text`-Ton misst 3,64–4,47:1 und bleibt unter der
// 4,5:1-Schwelle. Die Chips nehmen den `-soft`-Ton als Flaeche und den
// `-Text`-Ton als Schrift („`main` auf `soft`", 4,75 hell). Die mittleren
// Fuellungen (`#818cf8`, `warnTextDark`) tragen den Tinten-Ton, die dunklen
// bleiben bei Weiss. `draft` traegt den DECKENDEN Neutralton `washSelected`
// statt des Alpha-`ink45` (das im Dunkelmodus auf 1,11:1 kippt; deckend misst
// `ink` darauf 14,48:1 in beiden Modi) — die Probe verbietet Alpha-Fuellungen
// in `STATUS_COLORS`. Jeder Chip traegt einen 1-px-Rahmen (`Pill` nimmt `c100`, hier
// `divider`), denn die `-soft`-Flaechen messen gegen Weiss nur 1,04–1,09:1.
// Gemessen (WCAG, `rgba` ueber Weiss): 14 Paarungen, 4,75–10,69, alle ≥ 4,5:1.
const CHIP_TEXT_ON_DARK = '#fff'
export const STATUS_TEXT_COLORS = {
  draft: V2.ink,
  vendor_notified: V2.infoText,
  awaiting_response: CHIP_TEXT_ON_DARK,
  awaiting_customer_info: V2.warnText,
  partially_approved: V2.ink,
  approved: V2.okText,
  confirmed: CHIP_TEXT_ON_DARK,
  rejected: CHIP_TEXT_ON_DARK,
  escalated: V2.errText,
  resolved: CHIP_TEXT_ON_DARK,
  awaiting_return: V2.ink,
  in_transit: V2.infoText,
  received: V2.okText,
  reopened: V2.ink,
}
